import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

declare var RTA_API: string;

export interface EmailTemplate {
  id?: number;
  name: string;
  subject: string;
  body: string;
  eventType: string;
  isActive: boolean;
  createdBy?: number;
  createdAt?: Date;
  modifiedBy?: number;
  modifiedAt?: Date;
}

export interface DistributionList {
  id?: number;
  name: string;
  description?: string;
  eventType: string;
  isActive: boolean;
  createdBy?: number;
  createdAt?: Date;
  members?: DistributionListMember[];
}

export interface DistributionListMember {
  id?: number;
  distributionListId?: number;
  userId?: number;
  email: string;
  displayName?: string;
  isActive: boolean;
}

export interface NotificationLog {
  id: number;
  eventType: string;
  templateId?: number;
  recipients: string;
  subject: string;
  sentAt: Date;
  status: string;
  errorMessage?: string;
  relatedEntityId?: number;
  relatedEntityType?: string;
  sentByUserId?: number;
}

export interface NotificationLogResponse {
  logs: NotificationLog[];
  totalCount: number;
}

export interface SendTestEmailRequest {
  recipientEmail: string;
  variables?: { [key: string]: string };
}

@Injectable({
  providedIn: 'root'
})
export class NotificationAdminService {
  private baseUrl: string = RTA_API.replace('/RTA', '/Notification');

  constructor(private http: HttpClient) { }

  // Email Templates
  getTemplates(): Observable<EmailTemplate[]> {
    return this.http.get<EmailTemplate[]>(`${this.baseUrl}/templates`);
  }

  getTemplate(id: number): Observable<EmailTemplate> {
    return this.http.get<EmailTemplate>(`${this.baseUrl}/templates/${id}`);
  }

  createTemplate(template: EmailTemplate): Observable<EmailTemplate> {
    return this.http.post<EmailTemplate>(`${this.baseUrl}/templates`, template);
  }

  updateTemplate(id: number, template: EmailTemplate): Observable<EmailTemplate> {
    return this.http.put<EmailTemplate>(`${this.baseUrl}/templates/${id}`, template);
  }

  deleteTemplate(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/templates/${id}`);
  }

  previewTemplate(id: number, variables: { [key: string]: string }): Observable<{ html: string }> {
    return this.http.post<{ html: string }>(`${this.baseUrl}/templates/${id}/preview`, variables);
  }

  sendTestEmail(id: number, request: SendTestEmailRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/templates/${id}/send-test`, request);
  }

  // Distribution Lists
  getDistributionLists(): Observable<DistributionList[]> {
    return this.http.get<DistributionList[]>(`${this.baseUrl}/distribution-lists`);
  }

  getDistributionList(id: number): Observable<DistributionList> {
    return this.http.get<DistributionList>(`${this.baseUrl}/distribution-lists/${id}`);
  }

  createDistributionList(list: DistributionList): Observable<DistributionList> {
    return this.http.post<DistributionList>(`${this.baseUrl}/distribution-lists`, list);
  }

  updateDistributionList(id: number, list: DistributionList): Observable<DistributionList> {
    return this.http.put<DistributionList>(`${this.baseUrl}/distribution-lists/${id}`, list);
  }

  deleteDistributionList(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/distribution-lists/${id}`);
  }

  getDistributionListMembers(listId: number): Observable<DistributionListMember[]> {
    return this.http.get<DistributionListMember[]>(`${this.baseUrl}/distribution-lists/${listId}/members`);
  }

  addDistributionListMember(listId: number, member: DistributionListMember): Observable<DistributionListMember> {
    return this.http.post<DistributionListMember>(`${this.baseUrl}/distribution-lists/${listId}/members`, member);
  }

  removeDistributionListMember(listId: number, memberId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/distribution-lists/${listId}/members/${memberId}`);
  }

  // Event Types
  getEventTypes(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/event-types`);
  }

  // Notification Logs
  getLogs(options: {
    skip?: number;
    take?: number;
    eventType?: string;
    status?: string;
  } = {}): Observable<NotificationLogResponse> {
    let params = new HttpParams();

    if (options.skip !== undefined) {
      params = params.set('skip', options.skip.toString());
    }
    if (options.take !== undefined) {
      params = params.set('take', options.take.toString());
    }
    if (options.eventType) {
      params = params.set('eventType', options.eventType);
    }
    if (options.status) {
      params = params.set('status', options.status);
    }

    return this.http.get<NotificationLogResponse>(`${this.baseUrl}/logs`, { params });
  }

  getLog(id: number): Observable<NotificationLog> {
    return this.http.get<NotificationLog>(`${this.baseUrl}/logs/${id}`);
  }

  getLogsByEntity(entityType: string, entityId: number): Observable<NotificationLog[]> {
    return this.http.get<NotificationLog[]>(`${this.baseUrl}/logs/by-entity/${entityType}/${entityId}`);
  }
}
